/**
 * FieldEditHistory — compact per-field recent-changes list.
 *
 * Rendered inside edit mode (below the input, above Save/Cancel) for every
 * editable field. Only mounts when editing is open → no N+1 at rest.
 *
 * Props:
 *   entityId — the entity being edited
 *   field    — must match the ChangeLog.field column the backend stores
 *              (e.g. "licenseRenewal", "incorporationDateTyped",
 *               "director.name", "ownership_stake.ownerName")
 *
 * Query key: ["changelog", { entityId, field, limit: 5, hideSystem: true }]  (field-scoped, server-filtered)
 * i18n: "audit" namespace, keys:
 *   field_edit_history_recent  — "Recent changes"
 *   field_edit_history_empty   — "No changes recorded yet."
 *   see_full_history           — "See full history →"
 */

import { ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchChangelog } from "../lib/dataFetchers";
import { cn } from "../lib/cn";
import i18n from "../i18n";

interface FieldEditHistoryProps {
  entityId: string;
  /** Must match what the backend stores in ChangeLog.field for this editor. */
  field: string;
}

/** Inline relative-time formatter — simple, no extra deps. */
function relativeShort(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return i18n.t("audit:relative_just_now", { defaultValue: "just now" });
    if (mins < 60) return i18n.t("audit:relative_min_ago", { count: mins, defaultValue: `${mins} min ago` });
    const hours = Math.round(mins / 60);
    if (hours < 24) return i18n.t("audit:relative_hours_ago", { count: hours, defaultValue: `${hours}h ago` });
    const days = Math.round(hours / 24);
    if (days < 30) return i18n.t("audit:relative_days_ago", { count: days, defaultValue: `${days}d ago` });
    return d.toLocaleDateString(i18n.language || navigator.language);
  } catch {
    return iso;
  }
}

/** Truncates a value to 40 chars for the compact view. */
function truncate(val: string | null): string {
  if (val === null || val === "") return "—";
  return val.length > 40 ? `${val.slice(0, 40)}…` : val;
}

export function FieldEditHistory({ entityId, field }: FieldEditHistoryProps): JSX.Element {
  const { t } = useTranslation("audit");

  const { data, isLoading } = useQuery({
    queryKey: ["changelog", { entityId, field, limit: 5, hideSystem: true }],
    queryFn: () => fetchChangelog({ entityId, field, limit: 5, hideSystem: true }),
    // staleTime matches the pattern used elsewhere in this codebase
    staleTime: 0,
  });

  const items = data?.items ?? [];

  const fullHistoryHref = `/history?entityId=${encodeURIComponent(entityId)}&field=${encodeURIComponent(field)}`;

  return (
    <div
      className={cn(
        "mt-1.5 rounded-[8px] border border-border-2 bg-surface-2/60 px-3 py-2.5",
        "text-[12px] space-y-2",
      )}
      aria-label={t("field_edit_history_recent", {
        defaultValue: "Recent changes",
      })}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10.5px] font-bold tracking-[.05em] uppercase text-dim">
          {t("field_edit_history_recent", { defaultValue: "Recent changes" })}
        </span>
        <Link
          to={fullHistoryHref}
          className={cn(
            "inline-flex items-center gap-1 text-[11px] text-accent font-medium shrink-0",
            "hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/30 rounded",
          )}
          tabIndex={0}
        >
          {t("see_full_history", { defaultValue: "See full history →" })}
          <ExternalLink className="h-2.5 w-2.5" aria-hidden />
        </Link>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="skeleton h-4 w-full rounded" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-dim italic">
          {t("field_edit_history_empty", { defaultValue: "No changes recorded yet." })}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((item) => {
            const actor =
              item.user?.name ?? item.user?.email ?? t("attributed_to_system", { defaultValue: "System" });
            const when = relativeShort(item.createdAt);
            const oldVal = truncate(item.oldValue);
            const newVal = truncate(item.newValue);

            return (
              <li key={item.id} className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* old → new diff */}
                  <span
                    className="font-mono text-[11px] text-red-600 line-through max-w-[100px] truncate"
                    title={item.oldValue ?? undefined}
                  >
                    {oldVal}
                  </span>
                  <span className="text-dim" aria-hidden>→</span>
                  <span
                    className="font-mono text-[11px] text-green-700 font-medium max-w-[100px] truncate"
                    title={item.newValue ?? undefined}
                  >
                    {newVal}
                  </span>
                </div>
                <div className="text-[10.5px] text-dim">
                  {actor} · {when}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
